/**
 * The mission generator (03 §5): `generateMission(params)` builds a fresh, validated map
 * for any of the thirty-six archetypes in any of the three theatres - pure, and deterministic
 * in `params.seed`. `validateMission(def)` is the set of hard gates it holds itself to.
 *
 * One attempt: skeleton -> plan -> roads, linear obstacles, ravines -> the archetype's goal
 * compound -> the open end of the quiet way in (quietSide.ts) -> ponds and pod clearings -> the dog
 * teams (dogTeams.ts) -> the threat budget -> modifiers -> cover scatter -> the field grave, when there
 * are tags to recover.
 * A failed attempt is re-rolled with seed + 1 (up to 20 times); after that the budget is
 * relaxed by 10 % at a time. The best attempt is returned even if none was clean, so the
 * game always gets a map; in practice the first attempt passes.
 */

import { PI } from '../sim/math';
import { Pcg32 } from '../sim/rng';
import type { MissionDef, MissionParams } from '../sim/types';
import { placeAlternateLz } from './altLz';
import { ARCHETYPES } from './archetypes';
import { sliceBands } from './bands';
import { BIOME_KITS } from './biomes';
import { doublePatrols, spendBudget } from './budget';
import { MapBuilder } from './builder';
import { buildGoal } from './compound';
import { clockFor, effectiveParams, spawnCapFor, startAlertFor, windFor } from './conditions';
import { linkDogTeams, placeDogTeams } from './dogTeams';
import { collectCoverPoints } from './emplacements';
import { dressBelt } from './fieldworks';
import { buySentry } from './garrison';
import { dismissAbsentGuard, openGateOf, openInsideGate } from './insideMan';
import { buildDropHazards, buildLines, buildRavines } from './obstacles';
import { makePlan } from './plan';
import { collectPosts } from './posts';
import { planQuietApproach } from './quietSide';
import { buildRoads, reserveKeyGround } from './roads';
import { reserveClearings, scatterCover } from './scatter';
import { placeFieldGrave } from './tagRecovery';
import { BUDGET, LIMITS, MAP } from './tuning';
import type { GenCtx } from './types';
import { validateMission } from './validate';
import { buildPonds, buildRiver } from './water';

export { ARCHETYPES, ARCHETYPE_IDS, ORIGINAL_IDS } from './archetypes';
export { validateMission } from './validate';

const GEN_STREAM = 97;

/** One attempt. Returns the mission and whatever the generator itself could not do. */
function attempt(
  params: MissionParams, tryIndex: number, budgetScale: number,
): { def: MissionDef; problems: string[] } {
  const spec = ARCHETYPES[params.archetype];
  const kit = BIOME_KITS[params.biome];
  const effective = effectiveParams(params, kit);
  const sizeX = MAP.widthTiles * MAP.tile;
  const sizeZ = spec.lengthTiles * MAP.tile;
  const rng = new Pcg32((params.seed + tryIndex) >>> 0, GEN_STREAM);
  const bands = sliceBands(params.seed, sizeZ);
  const map = new MapBuilder(sizeX, sizeZ);
  const ctx: GenCtx = {
    params: effective, spec, kit, rng, sizeX, sizeZ, bands, map,
    plan: makePlan(rng.fork('plan'), bands, spec, kit, sizeX, effective.threat),
    posts: [], coverPoints: [], patrolGroups: [], quiet: { end: null, exposed: [], sight: null },
    elite: spec.garrison === 'elite' || effective.modifiers.includes('elite-garrison'),
    spawnCap: spawnCapFor(effective),
    budgetScale,
  };

  // Inside Man (04 §8): the gate is cut before anything is laid, so the perimeter and the belt go up
  // with the hole already in them - a gap opened afterwards would leave the props it removed standing.
  openInsideGate(ctx);
  reserveKeyGround(ctx);
  buildRoads(ctx);
  buildLines(ctx);
  buildRavines(ctx);
  buildDropHazards(ctx);
  if (spec.river) buildRiver(ctx);
  // With no compound the open stretch is known beforehand, and the border's own guns must respect it.
  if (spec.layout === 'border') planQuietApproach(ctx);
  const goal = buildGoal(ctx);
  if (spec.layout !== 'border') planQuietApproach(ctx);
  reserveClearings(ctx);
  buildPonds(ctx);

  ctx.posts = collectPosts(ctx);
  ctx.coverPoints.push(...collectCoverPoints(ctx));
  dressBelt(ctx);
  // ROUND8: the dog teams first, so the rest of the garrison is bought - and laid - around their beats.
  const dogs = placeDogTeams(ctx);
  spendBudget(ctx, dogs.prepaid);
  if (effective.modifiers.includes('high-alert')) doublePatrols(ctx);
  // A quiet sector still has a garrison: top it up to the minimum with sentries in the approaches.
  const topUp = rng.fork('minimum-garrison');
  for (let tries = 0; tries < 30 && map.spawns.length < LIMITS.spawnsMin; tries++) {
    buySentry(ctx, topUp, 1 + topUp.int(Math.max(1, bands.length - 3)), Infinity);
  }
  // Inside Man: after the top-up, or the minimum-garrison loop would simply buy the absent guard again.
  const guardGone = dismissAbsentGuard(ctx);
  linkDogTeams(ctx, dogs.teams);
  scatterCover(ctx);
  const goalZone = goal.goalZone ?? { ...ctx.plan.compound };
  // Last of all, so the map is the same with or without it.
  const dogTags = placeFieldGrave(ctx, goalZone);

  const drop = bands[0] ?? { kind: 'drop', z0: sizeZ - 60, z1: sizeZ - MAP.badlands };
  const def: MissionDef = {
    name: spec.name,
    archetype: params.archetype,
    seed: params.seed,
    biome: params.biome,
    timeOfDay: effective.timeOfDay,
    weather: effective.weather,
    sizeX,
    sizeZ,
    clockSeconds: clockFor(spec, effective, sizeZ),
    threat: effective.threat,
    startAlert: startAlertFor(spec, effective),
    modifiers: [...effective.modifiers],
    // ROUND5: the cards ride on the mission, not only on the parameters, or nothing downstream can read them.
    intel: [...effective.intel],
    trenches: map.trenches,
    roads: map.roads,
    ponds: map.ponds,
    waterBarriers: map.waterBarriers,
    mineFields: map.mineFields,
    props: map.props,
    spawns: map.spawns,
    captives: map.captives,
    objective: goal.objective,
    player: { x: ctx.plan.player.x, z: ctx.plan.player.z, facing: PI },
    dropBand: { z0: drop.z0, z1: drop.z1 },
    wind: windFor(rng.fork('wind'), effective),
    pickup: { ...ctx.plan.pickup },
    goalZone,
  };
  if (dogTags) def.dogTags = dogTags;
  // The two cards that put something on the ground: where the second X is, and where the gate was left open.
  const altLz = placeAlternateLz(ctx);
  if (altLz) def.pickupAlt = altLz;
  const openGate = openGateOf(ctx, guardGone);
  if (openGate) def.openGate = openGate;
  // Round three (10 §3): what the goal itself decides about the mission as a whole, then its last word.
  const extras = goal.mission ?? {};
  if (extras.startCaptured !== undefined) def.startCaptured = extras.startCaptured;
  if (extras.podsAt !== undefined) def.podsAt = extras.podsAt;
  if (extras.blackoutGroups !== undefined) def.blackoutGroups = extras.blackoutGroups;
  if (extras.player !== undefined) def.player = extras.player;
  goal.finish?.(def);
  return { def, problems: map.problems };
}

export interface GenerationReport {
  def: MissionDef;
  /** How many attempts it took (1 = the first passed every gate). */
  attempts: number;
  /** 1, or less if the budget had to be relaxed. */
  budgetScale: number;
  /** What was wrong with each rejected attempt; and with `def` itself in `faults` if nothing passed. */
  rejected: string[][];
  faults: string[];
}

/**
 * `generateMission`, with the story of how it got there: for tests, tools and tuning.
 * `extraGate` adds a caller's own hard gate to the validator's (a bot run, a design rule).
 */
export function generateMissionReport(
  params: MissionParams, extraGate?: (def: MissionDef) => string[],
): GenerationReport {
  let best: { def: MissionDef; faults: string[]; scale: number } | null = null;
  const rejected: string[][] = [];
  let tryIndex = 0;
  let scale = 1;
  for (let relax = 0; relax <= BUDGET.relaxations; relax++) {
    const tries = relax === 0 ? BUDGET.rerolls + 1 : 4;
    for (let i = 0; i < tries; i++) {
      const { def, problems } = attempt(params, tryIndex++, scale);
      const faults = problems.length > 0 ? problems : [...validateMission(def), ...(extraGate?.(def) ?? [])];
      if (faults.length === 0) return { def, attempts: tryIndex, budgetScale: scale, rejected, faults: [] };
      rejected.push(faults);
      if (!best || faults.length < best.faults.length) best = { def, faults, scale };
    }
    scale *= BUDGET.relax;
  }
  const last = best ?? { ...attempt(params, 0, 1), faults: ['no attempt was made'], scale: 1 };
  return { def: last.def, attempts: tryIndex, budgetScale: last.scale, rejected, faults: last.faults };
}

/** Build a mission for these parameters. Pure: the same parameters always give the same mission. */
export function generateMission(params: MissionParams): MissionDef {
  return generateMissionReport(params).def;
}
